import { useState } from "react"
import { Typography } from "@material-ui/core"
import dayjs from "dayjs"

import { formatDisplayScore } from "../../../utils/formatters"
import { colors } from "../../../utils/theme"

interface ChartTooltipProps {
  score: number
  scoreType: string
  completedAt: string
  label: string
  children: React.ReactNode
}

/**
 * ScoringChartTooltip is a component that wraps arbitrary content and displays
 * a hover tooltip with questionnaire scoring details.
 *
 * @param {Object} props - The props object.
 * @param {number} props.score - The numeric score value to display.
 * @param {string} props.scoreType - The score type label (e.g., "PHQ-9", "GAD-7").
 * @param {string} props.completedAt - ISO date/time string representing when the questionnaire was completed.
 * @param {string} props.label - A short label shown in the tooltip (typically severity text).
 * @param {React.ReactNode} props.children - The element(s) that trigger the tooltip on hover.
 */
export default function ScoringChartTooltip({
  score,
  scoreType,
  completedAt,
  label,
  children,
}: ChartTooltipProps) {
  const [isTooltipVisible, setTooltipVisible] = useState(false)
  const formattedScore = formatDisplayScore(score, scoreType)

  const handleMouseEnter = () => {
    setTooltipVisible(true)
  }

  const handleMouseLeave = () => {
    setTooltipVisible(false)
  }

  return (
    <div
      style={{ position: "relative", display: "flex", flexDirection: "column" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isTooltipVisible && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            backgroundColor: colors.neutral.space,
            color: "#fff",
            padding: "8px",
            borderRadius: "4px",
            zIndex: "999",
            whiteSpace: "nowrap",
            marginTop: "8px",
          }}
        >
          <div>
            {scoreType} Score: {formattedScore}
          </div>
          <div>
            <Typography
              variant="caption"
              style={{
                fontStyle: "italic",
                color: "#FFFFFF",
                fontWeight: 400,
                fontSize: 13,
                lineHeight: 0.7,
              }}
            >
              {label}
            </Typography>
          </div>
          <div>
            <Typography
              variant="caption"
              style={{
                fontStyle: "italic",
                color: "#FFFFFF",
                fontWeight: 400,
                fontSize: 13,
                lineHeight: 0.7,
              }}
            >
              {dayjs(completedAt).format("MMM DD, YYYY")}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}
